import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useFloating,
  arrow,
  autoUpdate,
  offset,
  flip,
  safePolygon,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useClientPoint,
} from '@floating-ui/react';
import { TooltipProps } from './types';
import { useId } from '@redsift/design-system';

export function useTooltip({
  color,
  defaultOpen,
  delay,
  placement,
  isOpen: propsIsOpen,
  offset: propsOffset,
  onOpen,
  tooltipId: propsTooltipId,
  triggerClassName,
}: Omit<TooltipProps, 'children'>) {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(propsIsOpen ?? defaultOpen);

  const [_id] = useId();
  const tooltipId = propsTooltipId ?? _id;

  useEffect(() => {
    setIsOpen(propsIsOpen ?? defaultOpen);
  }, [propsIsOpen, defaultOpen]);

  const handleOpen = useCallback(
    (collapsed: boolean) => {
      if (onOpen) {
        onOpen(collapsed);
      }
      if (propsIsOpen === undefined || propsIsOpen === null) {
        setIsOpen(collapsed);
      }
    },
    [onOpen]
  );

  const data = useFloating({
    placement: placement === 'client-point' ? 'left' : placement,
    open: isOpen,
    onOpenChange: handleOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(propsOffset ?? 8),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;
  const clientPoint = useClientPoint(context, { enabled: placement === 'client-point' });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay,
      close: 0,
    },
    handleClose: safePolygon(),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role, clientPoint]);

  return React.useMemo(
    () => ({
      color,
      isOpen,
      handleOpen,
      ...interactions,
      ...data,
      arrowRef,
      tooltipId,
      triggerClassName,
    }),
    [color, isOpen, handleOpen, interactions, data, arrowRef, tooltipId, triggerClassName]
  );
}
